import { RefObject } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Filter, X } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';

/** CR-3953: Filter options for the supplier list */
export interface SupplierFilters {
  site: string;
  status: string;
  supplier_code: string;
  commodity_group: string;
  commodity_type: string;
  strategic: string;
  risk: string;
  questionnaire: string;
}

export const EMPTY_FILTERS: SupplierFilters = {
  site: '',
  status: '',
  supplier_code: '',
  commodity_group: '',
  commodity_type: '',
  strategic: '',
  risk: '',
  questionnaire: '',
};

const RISK_QUICK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const QUESTIONNAIRE_QUICK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'up_to_date', label: 'Up to Date' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
  { value: 'none', label: 'Not Assigned' },
];

interface SearchFilterBarProps {
  searchRef: RefObject<HTMLInputElement | null>;
  showFilters: boolean;
  filters?: SupplierFilters;
  riskCounts?: Record<string, number>;
  questionnaireCounts?: Record<string, number>;
  siteOptions?: { value: string; label: string }[];
  commodityGroupOptions?: { value: string; label: string }[];
  commodityTypeOptions?: { value: string; label: string }[];
  supplierCodeOptions?: { value: string; label: string }[];
  searchQuery?: string;
  onSearch: () => void;
  onSearchKeyDown: (e: React.KeyboardEvent) => void;
  onClearSearch?: () => void;
  onToggleFilters: () => void;
  onFiltersChange?: (filters: SupplierFilters) => void;
}

export function SearchFilterBar({
  searchRef,
  showFilters,
  filters = EMPTY_FILTERS,
  riskCounts = {},
  questionnaireCounts = {},
  siteOptions = [],
  commodityGroupOptions = [],
  commodityTypeOptions = [],
  supplierCodeOptions = [],
  searchQuery = '',
  onSearch,
  onSearchKeyDown,
  onClearSearch,
  onToggleFilters,
  onFiltersChange,
}: SearchFilterBarProps) {
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handleFilterChange = (key: keyof SupplierFilters, value: string) => {
    if (key === 'commodity_group') {
      onFiltersChange?.({ ...filters, commodity_group: value, commodity_type: '' });
    } else {
      onFiltersChange?.({ ...filters, [key]: value });
    }
  };

  const handleClearFilters = () => {
    onFiltersChange?.({ ...EMPTY_FILTERS });
  };

  return (
    <div className="space-y-4">
      {/* Search bar + filter toggle */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            placeholder="Search suppliers..."
            onKeyDown={onSearchKeyDown}
            className="pl-10 pr-10 border border-input bg-background"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                if (searchRef.current) searchRef.current.value = '';
                onClearSearch?.();
              }}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button onClick={onSearch} variant="outline">
          <Search className="h-4 w-4" />
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={onToggleFilters}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      {/* Quick filter chip rows — Risk + Questionnaire Status */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">Risk</span>
          {RISK_QUICK_OPTIONS.map((opt) => {
            const count = riskCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all-risk'}
                type="button"
                onClick={() => handleFilterChange('risk', opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filters.risk === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">Questionnaire</span>
          {QUESTIONNAIRE_QUICK_OPTIONS.map((opt) => {
            const count = questionnaireCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all-q'}
                type="button"
                onClick={() => handleFilterChange('questionnaire', opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filters.questionnaire === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Inline collapsible filter panel (matching Purchasing page design) */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Filter Suppliers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Company Site */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Company Site
                  {siteOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}
                </label>
                {siteOptions.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-muted-foreground text-sm italic cursor-not-allowed">
                    No company sites available
                  </div>
                ) : (
                  <SearchableSelect
                    options={siteOptions}
                    value={filters.site}
                    onChange={(val) => handleFilterChange('site', val)}
                    placeholder="All Company Sites"
                  />
                )}
              </div>

              {/* Supplier Code */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Supplier Code
                  {supplierCodeOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}
                </label>
                {supplierCodeOptions.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-muted-foreground text-sm italic cursor-not-allowed">
                    No supplier codes available
                  </div>
                ) : (
                  <SearchableSelect
                    options={supplierCodeOptions}
                    value={filters.supplier_code}
                    onChange={(val) => handleFilterChange('supplier_code', val)}
                    placeholder="Search by code or name…"
                  />
                )}
              </div>

              {/* Commodity Group */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Commodity Group
                  {commodityGroupOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}
                </label>
                {commodityGroupOptions.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-muted-foreground text-sm italic cursor-not-allowed">
                    No commodity groups available
                  </div>
                ) : (
                  <select
                    value={filters.commodity_group}
                    onChange={(e) => handleFilterChange('commodity_group', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  >
                    <option value="">All Commodity Groups</option>
                    {commodityGroupOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Commodity Type */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Commodity Type
                  {commodityTypeOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}
                </label>
                {commodityTypeOptions.length === 0 ? (
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-muted-foreground text-sm italic cursor-not-allowed">
                    No commodity types available
                  </div>
                ) : (
                  <select
                    value={filters.commodity_type}
                    onChange={(e) => handleFilterChange('commodity_type', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  >
                    <option value="">All Commodity Types</option>
                    {commodityTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Risk Level */}
              <div>
                <label className="text-sm font-medium mb-2 block">Risk Level</label>
                <select
                  value={filters.risk}
                  onChange={(e) => handleFilterChange('risk', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  {RISK_QUICK_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all-risk'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Questionnaire Status */}
              <div>
                <label className="text-sm font-medium mb-2 block">Questionnaire Status</label>
                <select
                  value={filters.questionnaire}
                  onChange={(e) => handleFilterChange('questionnaire', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                >
                  {QUESTIONNAIRE_QUICK_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all-q'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Strategic */}
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.strategic === 'yes'}
                    onChange={(e) => handleFilterChange('strategic', e.target.checked ? 'yes' : '')}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">Strategic Only</span>
                </label>
              </div>
            </div>

            {/* Search + Clear Buttons */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              {activeFilterCount > 0 && (
                <Button variant="outline" onClick={handleClearFilters} className="px-6">
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              )}
              <Button onClick={onSearch} className="px-6">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
